import React, { useEffect, useState, useCallback } from 'react';
import { TextField, Typography, Button, Container, FormHelperText, Grid, Autocomplete, InputAdornment, } from '@mui/material';
import ImageCropModal, { useImageCrop } from '../../../Component/ImageCropModal/ImageCropModal';
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from 'react-router-dom';
import { useSavePartnerMutation, useLazyGetPartnerByIdQuery, useUpdatePartnerMutation, useLazyGetGroupOfPartnerQuery } from '../../../app/services/partnerService';
import { toast } from "react-toastify";
import Spinner from "../../../Component/spinner/spinner";
import { useSelector } from 'react-redux';
import InfoIcon from '@mui/icons-material/Info';
import { selectCurrentScope } from "../../../app/auth/authSlice";

const AddEditPartner = () => {
    const { register, watch, handleSubmit, setValue, reset, formState: { errors }, control, clearErrors } = useForm({
        defaultValues: { logo: '', description: '', websiteLink: '', partnerName: '', location: '', fromYear: '', toYear: '' }, mode: 'onChange'
    });
    const { cropModal, openCropModal, handleCropApply, closeCropModal } = useImageCrop();
    const [savePartner] = useSavePartnerMutation();
    const [getPartnerById] = useLazyGetPartnerByIdQuery();
    const [UpdatePartner] = useUpdatePartnerMutation();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const memberScope = useSelector(selectCurrentScope);
    const [imageErrorMessage, setImageErrorMessage] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [isValidImage, setIsValidImage] = useState(true);
    const [getAll] = useLazyGetGroupOfPartnerQuery()
    const [getAllPartner, setGetAllPartners] = useState([]);

    // Handle image file change
    const handleImageFileChange = (event) => {
        openCropModal(event, null, 5); // 5 MB max
    };

    const handleCropSuccess = (base64) => {
        handleCropApply(base64, (b64) => {
            setSelectedFile(b64);
            setValue("logo", b64, { shouldValidate: true, shouldDirty: true });
            setIsValidImage(true);
            setImageErrorMessage("");
            clearErrors("image");
        });
    };

    const fetchGetAll = useCallback(async () => {
        try {
            const res = await getAll();
            setGetAllPartners(res?.data?.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [getAll]);

    useEffect(() => {
        fetchGetAll();
    }, [fetchGetAll, getAllPartner]);


    useEffect(() => {

    }, [imageErrorMessage])

    // Save partner
    const onSavePartner = (data) => {
        if (!isValidImage) {
            return; // Stop the form submission if the image is invalid or greater than 2MB
        }
        setLoading(true);

        // Regex to check if logo is a base64 string
        const base64Regex = /^data:image\/(?:png|jpeg|jpg|gif);base64,[\w+/=]+$/;

        // Validate the logo and ensure it is either base64 or a URL
        const logo = base64Regex.test(data?.logo)
            ? data?.logo
            : typeof data?.logo === 'string' ? data?.logo.split("/").slice(-2).join("/") : '';

        // Prepare partner data
        const partnerData = {
            description: data.description,
            websiteLink: data.websiteLink,
            id: data._id,
            logo: selectedFile || logo, // Ensure logo is correctly assigned
            partnerName: data.partnerName,
            fromYear: data.fromYear,
            toYear: data.toYear,
            location: data.location,
            category: data.category
        };

        // Determine the save action (update or create)
        const saveAction = id ? UpdatePartner(partnerData) : savePartner(partnerData);

        // Execute the save action
        saveAction
            .then((res) => {
                if (res?.data?.status) {
                    toast.success(id ? "Partner Updated Successfully!!!" : "Partner Created Successfully!!!");
                    reset();
                    navigate(getRedirectPath());
                } else {
                    toast.error(res?.data?.err?.message || "An error occurred.");
                }
                setLoading(false);
            })
            .catch((err) => {
                toast.error("Error occurred while saving partner.");
                setLoading(false);
                console.error(err);
            });
    };



    // Get redirect path based on user scope
    const getRedirectPath = () => {
        if (memberScope === "PORTAL_SUPER_ADMIN") return "/admin/partner-table";
        return "/assistantadmin/partner-table"; // Default
    };

    // Get data for editing
    useEffect(() => {
        if (id) {
            getPartnerById(id).then((res) => {
                if (res?.data?.status) {
                    setSelectedFile(res?.data?.data?.logo);
                    Object.entries(res?.data?.data).forEach(([key, value]) => {
                        setValue(key, value);
                    });
                }
            });
        }
    }, [getPartnerById, id, setValue]);


    return (
        <>
            <div className="pt-5">
                <div className="chapter-heading">
                    <h2 className="text-center title-font text-light py-3 mt-1">{id ? "Edit Our Partner" : "Add  Our Partner"}</h2>
                </div>
            </div>
            <Container maxWidth={'xxl'} className='p-md-5 p-3 overallPadding' >
                <div className='w-100 text-end mb-3'>
                    <Button
                        onClick={() => window.history.back()}
                        variant="contained"
                        className="menus-color"
                    >
                        Back
                    </Button>
                </div>
                {loading ? <Spinner /> : (
                    <form className='w-100 p-0 m-0' onSubmit={handleSubmit(onSavePartner)}>
                        <Grid container rowSpacing={3} columnSpacing={3}>
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    id="partnerName"
                                    label={<Typography className="text-dark">Partner Name <span style={{ color: "red" }}>*</span></Typography>}
                                    variant="outlined"
                                    placeholder=" Enter Partner Name"
                                    {...register("partnerName", {
                                        required: "Please enter Partner Name",
                                        validate: (value) => value.trim().length > 0 || "Empty value is not allowed"
                                    })}
                                    error={!!errors?.partnerName}
                                    helperText={errors?.partnerName?.message}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <Controller
                                    name="category"
                                    control={control}
                                    rules={{
                                        required: "Please select a category",
                                        validate: (value) => value.trim().length > 0 || "Empty value is not allowed"
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                        <Autocomplete
                                            options={getAllPartner || []}
                                            getOptionLabel={(option) => option?.category || ""}
                                            value={getAllPartner?.find((option) => option?.category === value) || null}
                                            freeSolo
                                            onChange={(_, newValue) => {
                                                const selectedCategory = newValue?.category ?? "";
                                                onChange(selectedCategory);
                                                setValue("category", selectedCategory, { shouldValidate: true }); // ✅ Ensure validation triggers
                                            }}
                                            onInputChange={(_, inputValue) => {
                                                onChange(inputValue);
                                                setValue("category", inputValue, { shouldValidate: true }); // ✅ Keep validation in sync
                                            }}

                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={<Typography className="text-dark">Choose a Category <span style={{ color: "red" }}>*</span></Typography>}
                                                    error={Boolean(errors?.category)}
                                                    helperText={errors?.category?.message}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        style: {
                                                            height: 55, // ✅ Adjust height manually
                                                            padding: "0 12px", // ✅ Adjust padding manually
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />




                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    id="location"
                                    label={<Typography className="text-dark">Location <span style={{ color: "red" }}>*</span></Typography>}
                                    variant="outlined"
                                    placeholder=" Enter Location"
                                    {...register("location", {
                                        required: "Please enter Location Name",
                                        validate: (value) => value.trim().length > 0 || "Empty value is not allowed"
                                    })}
                                    error={!!errors?.location}
                                    helperText={errors?.location?.message}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    id="fromYear"
                                    label={
                                        <Typography className="text-dark">
                                            From Year <span style={{ color: "red" }}>*</span>
                                        </Typography>
                                    }
                                    variant="outlined"
                                    placeholder="Enter From Year"
                                    inputProps={{ maxLength: 4 }} // Restricts input length to 4 characters
                                    {...register("fromYear", {
                                        required: "Please enter From Year",
                                        maxLength: {
                                            value: 4,
                                            message: "From Year cannot exceed 4 digits",
                                        },
                                        pattern: {
                                            value: /^[0-9]{4}$/, // Ensures only 4-digit numeric input
                                            message: "Please enter a valid 4-digit year",
                                        },

                                    })}
                                    error={!!errors?.fromYear}
                                    helperText={errors?.fromYear?.message}
                                />

                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    id="fromYear"
                                    label={
                                        <Typography className="text-dark">
                                            To Year <span style={{ color: "red" }}>*</span>
                                        </Typography>
                                    }
                                    variant="outlined"
                                    placeholder="Enter To Year"
                                    inputProps={{ maxLength: 4 }} // Restricts input length to 4 characters
                                    {...register("toYear", {
                                        required: "Please enter To Year",
                                        maxLength: {
                                            value: 4,
                                            message: "From Year cannot exceed 4 digits",
                                        },
                                        pattern: {
                                            value: /^[0-9]{4}$/, // Ensures only 4-digit numeric input
                                            message: "Please enter a valid 4-digit year",
                                        },
                                        validate: (value) =>
                                            value >= watch("fromYear") || "To Year cannot be less than From Year",
                                    })}
                                    error={!!errors?.toYear}
                                    helperText={errors?.toYear?.message}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    id="websiteLink"
                                    label={<Typography className="text-dark">Website Link <span style={{ color: "red" }}>*</span></Typography>}
                                    variant="outlined"
                                    placeholder="Example:www.sample.com"
                                    {...register("websiteLink", {
                                        required: "Please enter website link",
                                        pattern: {
                                            value: /^(https?:\/\/)?((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|(\d{1,3}\.){3}\d{1,3})(:[0-9]+)?(\/[\w .-]*)*(\?.*)?$/,
                                            message: "Please enter a valid URL",
                                        },
                                    })}
                                    error={!!errors?.websiteLink}
                                    helperText={errors?.websiteLink?.message}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    id="description"
                                    InputProps={{
                                        endAdornment:
                                            <InputAdornment position='end'>
                                                <span className='text-secondary-new fw-bold mt-5'>{100 - watch('description').split(' ').filter(v => v.trim().length > 0).length}</span>
                                            </InputAdornment>
                                    }}
                                    label={
                                        <Typography className="text-dark">
                                            Description <span style={{ color: "red" }}>*</span>
                                        </Typography>
                                    }
                                    variant="outlined"
                                    placeholder="Enter Description"
                                    {...register("description", {
                                        required: "Please enter description",
                                        validate: (value) => {
                                            if (!value || value.trim() === "") {
                                                return "Empty value is not allowed";
                                            }
                                            const words = value.trim().split(/\s+/).filter(word => word.length > 0);
                                            const wordCount = words.length;
                                            if (wordCount > 100) {
                                                return `Description can contain up to 100 words.Current: ${wordCount}`;
                                            }
                                            return true;
                                        },
                                    })}
                                    error={!!errors?.description}
                                    helperText={errors?.description?.message}
                                />
                                <FormHelperText className='d-flex'><InfoIcon style={{ color: '#611f1e' }} />
                                    <p style={{ fontSize: '14px', display: 'flex', alignItems: 'center', marginBottom: '0px' }}>Description can contain up to 100 words.</p></FormHelperText>

                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <Typography className="text-dark fw-bold align-self-start mb-2" style={{ fontSize: '16px' }}>
                                    Logo <span style={{ color: "red" }}>*</span>
                                </Typography>
                                <div className="d-flex align-items-center gap-2">

                                    {selectedFile ? (
                                        <img
                                            src={selectedFile}
                                            alt="Logo Preview"
                                            height="150"
                                            style={{ objectFit: "contain", maxWidth: "100%", maxHeight: "150px" }}
                                            className="img-responsive"
                                        />
                                    ) : (
                                        <div style={{
                                            width: "150px",
                                            height: "150px",
                                            border: "1px dashed #ccc",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#888",
                                            fontSize: "14px",
                                            backgroundColor: "#f9f9f9"
                                        }}>
                                            No Logo Selected
                                        </div>
                                    )}

                                    <TextField
                                        inputProps={{
                                            accept: "image/jpeg, image/png",
                                        }}
                                        id="thumbnailImage"
                                        type="file"
                                        style={{ display: "none" }}
                                        {...register("image", {
                                            required: { value: !id && !selectedFile, message: 'Please select an image' },
                                            onChange: (e) => handleImageFileChange(e),
                                        })}
                                    />

                                    <div className="d-flex justify-content-center">
                                        <label
                                            htmlFor="thumbnailImage"
                                        >
                                            <Button
                                                variant="contained"
                                                component="span"
                                                style={{ whiteSpace: "nowrap" }}
                                                className="menus-color mt-2"
                                            >
                                                Choose File
                                            </Button>
                                        </label>
                                    </div>

                                    {imageErrorMessage && (
                                        <FormHelperText error={true}>
                                            {imageErrorMessage}
                                        </FormHelperText>
                                    )}
                                    {errors?.image && (
                                        <FormHelperText error={true}>
                                            {errors?.image?.message}
                                        </FormHelperText>
                                    )}
                                </div>
                            </Grid>
                            <Grid item xs={12} className='text-center'>
                                <Button variant="contained" className="menus-color mb-5" type="submit" sx={{ mr: 3 }}>
                                    {id ? "Update" : "Submit"}
                                </Button>

                            </Grid>
                        </Grid>

                    </form>
                )}
            </Container >
            <ImageCropModal
                open={cropModal.open}
                imageSrc={cropModal.src}
                outputWidth={150}
                outputHeight={150}
                isSquareCrop={false}
                title="Crop Partner Logo"
                onCancel={closeCropModal}
                onCrop={handleCropSuccess}
            />
        </>
    );
};

export default AddEditPartner;
